// src/context/AlertsContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { CountyContext } from "../components/CountyContext";

const AlertsContext = createContext();

// Higher number = show earlier within the same tier (used in NwsAlerts)
const getSubtypeSeverityRank = (feature) => {
  const props = feature?.properties || {};
  const rawEvent = (props.event || "").toLowerCase();
  const rawHeadline = (props.headline || "").toLowerCase();
  const txt = `${rawEvent} ${rawHeadline}`;

  // Example: Winter storm vs extreme cold
  const isWinterStorm =
    txt.includes("winter storm") ||
    txt.includes("winter weather") ||
    txt.includes("blizzard") ||
    txt.includes("snow squall");

  const isExtremeCold =
    txt.includes("extreme cold") ||
    txt.includes("wind chill") ||
    txt.includes("cold weather advisory");

  if (isWinterStorm) return 2; // more impactful in your UI
  if (isExtremeCold) return 1; // less than winter storm

  return 0; // everything else
};

export const AlertsProvider = ({ children, countyMapping }) => {
  const { selectedCounty } = useContext(CountyContext);
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [flags, setFlags] = useState({
    hasBurnBan: false,
    hasWinterStorm: false,    // any winter hazard
    hasWinterWatch: false,    // winter watch/advisory tier
    hasWinterWarning: false,  // winter warning tier
    hasSevereStorm: false,
    hasFlooding: false,
    hasFog: false,
    hasFireWeather: false,
    hasHighWinds: false,
    hasExtremeCold: false,
    hasSurfHazard: false,
  });

  // Indicates whether flags represent the latest fetched alerts
  const [flagsReady, setFlagsReady] = useState(false);

  const resetFlags = () => {
    setFlags({
      hasBurnBan: false,
      hasWinterStorm: false,
      hasWinterWatch: false,
      hasWinterWarning: false,
      hasSevereStorm: false,
      hasFlooding: false,
      hasFog: false,
      hasFireWeather: false,
      hasHighWinds: false,
      hasExtremeCold: false,
      hasSurfHazard: false,
    });
    setFlagsReady(false);
  };

  const updateDerivedFlags = (nextAlerts) => {
    let hasBurnBan = false;
    let hasWinterStorm = false;
    let hasWinterWatch = false;
    let hasWinterWarning = false;
    let hasSevereStorm = false;
    let hasFlooding = false;
    let hasFog = false;
    let hasFireWeather = false;
    let hasHighWinds = false;
    let hasExtremeCold = false;
    let hasSurfHazard = false;

    nextAlerts.forEach((a) => {
      if (a.type === "burnBan") {
        hasBurnBan = true;
        return;
      }

      const props = a.raw && a.raw.properties;
      if (!props) return;

      const rawEvent = props.event || "";
      const rawHeadline = props.headline || "";
      const rawDesc = props.description || "";

      const event = rawEvent.toLowerCase();
      const headline = rawHeadline.toLowerCase();
      const desc = rawDesc.toLowerCase();
      const text = `${headline} ${desc}`;

      console.log("[AlertsContext] feature:", {
        event: props.event,
        headline: props.headline,
        description: props.description,
      });

      console.log("NWS event:", props.event);

      // ---------------- TITLE-FIRST LOGIC ----------------

      // WINTER WEATHER – explicit winter products in event/headline
      const isWinterWarningTitle =
        rawEvent.includes("Winter Storm Warning") ||
        rawEvent.includes("Blizzard Warning") ||
        rawEvent.includes("Ice Storm Warning") ||
        rawEvent.includes("Snow Squall Warning") ||
        rawHeadline.includes("Winter Storm Warning") ||
        rawHeadline.includes("Blizzard Warning") ||
        rawHeadline.includes("Ice Storm Warning") ||
        rawHeadline.includes("Snow Squall Warning");

      const isWinterWatchTitle =
        rawEvent.includes("Winter Storm Watch") ||
        rawEvent.includes("Winter Weather Advisory") ||
        rawEvent.includes("Blizzard Watch") ||
        rawEvent.includes("Freezing Rain Advisory") ||
        rawEvent.includes("Freezing Drizzle Advisory") ||
        rawHeadline.includes("Winter Storm Watch") ||
        rawHeadline.includes("Winter Weather Advisory") ||
        rawHeadline.includes("Blizzard Watch") ||
        rawHeadline.includes("Freezing Rain Advisory") ||
        rawHeadline.includes("Freezing Drizzle Advisory");

      if (isWinterWarningTitle) {
        hasWinterStorm = true;
        hasWinterWarning = true;
      }

      if (isWinterWatchTitle) {
        hasWinterStorm = true;
        if (!hasWinterWarning) {
          hasWinterWatch = true;
        }
      }

      // SURF / RIP CURRENT – title
      const isSurfTitle =
        rawEvent.includes("High Surf") ||
        rawEvent.includes("Rip Current") ||
        rawEvent.includes("Beach Hazards") ||
        rawHeadline.includes("High Surf") ||
        rawHeadline.includes("Rip Current") ||
        rawHeadline.includes("Beach Hazards");

      if (isSurfTitle) {
        hasSurfHazard = true;
      }

      // EXTREME COLD – cold-only titles
      const isColdTitle =
        rawEvent.includes("Freeze") ||
        rawEvent.includes("Frost") ||
        rawEvent.includes("Wind Chill") ||
        rawEvent.includes("Extreme Cold") ||
        rawEvent.includes("Cold Weather Advisory") ||
        rawHeadline.includes("Freeze") ||
        rawHeadline.includes("Frost") ||
        rawHeadline.includes("Wind Chill") ||
        rawHeadline.includes("Extreme Cold") ||
        rawHeadline.includes("Cold Weather Advisory");

      if (isColdTitle) {
        hasExtremeCold = true;
      }

      // SEVERE STORMS – title
      const isSevereTitle =
        rawEvent.includes("Severe Thunderstorm") ||
        rawEvent.includes("Tornado") ||
        rawEvent.includes("Flash Flood") ||
        rawHeadline.includes("Severe Thunderstorm") ||
        rawHeadline.includes("Tornado") ||
        rawHeadline.includes("Flash Flood");

      if (isSevereTitle) {
        hasSevereStorm = true;
      }

      // FLOODING – title
      const isFloodTitle =
        rawEvent.includes("Flood") || rawHeadline.includes("Flood");

      if (isFloodTitle) {
        hasFlooding = true;
      }

      // FOG – title
      const isFogTitle =
        rawEvent.includes("Dense Fog") ||
        rawEvent.includes("Fog Advisory") ||
        rawHeadline.includes("Dense Fog") ||
        rawHeadline.includes("Fog Advisory");

      if (isFogTitle) {
        hasFog = true;
      }

      // FIRE WEATHER – title
      const isFireTitle =
        rawEvent.includes("Red Flag") ||
        rawEvent.includes("Fire Weather") ||
        rawHeadline.includes("Red Flag") ||
        rawHeadline.includes("Fire Weather");

      if (isFireTitle) {
        hasFireWeather = true;
      }

      // HIGH WINDS – title
      const isWindTitle =
        rawEvent.includes("High Wind") ||
        rawEvent.includes("Wind Advisory") ||
        rawHeadline.includes("High Wind") ||
        rawHeadline.includes("Wind Advisory");

      if (isWindTitle) {
        hasHighWinds = true;
      }

      // ---------------- DESCRIPTION-BASED REFINEMENTS ----------------

      // Generic winter language in text
      if (
        event.includes("winter storm") ||
        event.includes("blizzard") ||
        event.includes("winter weather advisory") ||
        text.includes("snow") ||
        text.includes("freezing rain") ||
        text.includes("freezing drizzle") ||
        text.includes("sleet")
      ) {
        hasWinterStorm = true;
      }

      // SPS mentioning wintry precip → treat as watch tier
      if (event.includes("special weather statement")) {
        if (
          text.includes("snow") ||
          text.includes("freezing rain") ||
          text.includes("freezing drizzle") ||
          text.includes("sleet")
        ) {
          hasWinterStorm = true;
          if (!hasWinterWarning) {
            hasWinterWatch = true;
          }
        }
      }

      // Surf / Rip in text
      if (text.includes("high surf") || text.includes("rip current")) {
        hasSurfHazard = true;
      }

      // Extreme cold in text (complements title)
      if (
        text.includes("freeze") ||
        text.includes("frost") ||
        text.includes("dangerously cold") ||
        text.includes("bitterly cold") ||
        text.includes("wind chill")
      ) {
        hasExtremeCold = true;
      }

      // Flooding in text
      if (
        text.includes("flash flooding") ||
        text.includes("flash flood") ||
        text.includes("heavy rain")
      ) {
        hasFlooding = true;
      }

      // Fog in text
      if (text.includes("dense fog") || text.includes("fog")) {
        hasFog = true;
      }

      // Fire weather in text
      if (
        text.includes("red flag conditions") ||
        text.includes("critical fire weather") ||
        text.includes("wildfire")
      ) {
        hasFireWeather = true;
      }

      // High winds in text
      if (
        text.includes("strong winds") ||
        text.includes("damaging winds") ||
        text.includes("gusts") ||
        text.includes("high profile vehicles") ||
        /gusts?\s+(up to\s+)?\d{2}\s*mph/.test(text)
      ) {
        hasHighWinds = true;
      }

      // SPS add-ons for fire/wind/fog
      if (event.includes("special weather statement")) {
        if (
          text.includes("elevated fire danger") ||
          text.includes("critical fire weather") ||
          text.includes("wildfire")
        ) {
          hasFireWeather = true;
        }

        if (
          text.includes("strong winds") ||
          text.includes("damaging winds") ||
          text.includes("gusts") ||
          text.includes("high profile vehicles") ||
          /gusts?\s+(up to\s+)?\d{2}\s*mph/.test(text)
        ) {
          hasHighWinds = true;
        }

        if (text.includes("dense fog") || text.includes("fog")) {
          hasFog = true;
        }
      }
    });

    const nextFlags = {
      hasBurnBan,
      hasWinterStorm,
      hasWinterWatch,
      hasWinterWarning,
      hasSevereStorm,
      hasFlooding,
      hasFog,
      hasFireWeather,
      hasHighWinds,
      hasExtremeCold,
      hasSurfHazard,
    };

    console.log(
      "[AlertsContext] flags resolved for alerts:",
      nextAlerts.map((a) => a.raw?.properties?.event),
      "→",
      nextFlags
    );

    setFlags(nextFlags);
    setFlagsReady(true);
    console.log("[AlertsContextFlags] flagsReady set to true");
  };

  const fetchAlerts = async () => {
    if (!selectedCounty) {
      setAlerts([]);
      resetFlags();
      return;
    }

    setIsLoading(true);
    setFlagsReady(false);

    try {
      const mapping = countyMapping || [];
      const selectedCountyData = mapping.find(
        (c) => c.county === selectedCounty
      );

      if (!selectedCountyData) {
        setAlerts([]);
        resetFlags();
        return;
      }

      // 1. NWS alerts
      const nwsRes = await fetch(
        `https://api.weather.gov/alerts/active?zone=${selectedCountyData.nws_county_code}`
      );
      if (!nwsRes.ok) throw new Error("NWS network error");
      const nwsJson = await nwsRes.json();

      const weatherAlerts = (nwsJson.features || []).map((feature) => ({
        id: feature.id,
        type: "weather",
        title:
          (feature.properties && feature.properties.headline) ||
          "Weather alert",
        severity: "warning",
        source: "nws",
        raw: feature,
        // NEW: simple subtype severity to help ordering in NwsAlerts
        eventSubtypeSeverity: getSubtypeSeverityRank(feature),
      }));

      // 2. Burn ban
      const burnRes = await fetch(
        `https://tfsgis.tfs.tamu.edu/arcgis/rest/services/BurnBan/BurnBan/MapServer/0/query?f=json&where=County%20%3D%20'${selectedCounty}'%20AND%20BurnBan%20%3D%20'Yes'&returnGeometry=false&outFields=County,BurnBan`
      );
      if (!burnRes.ok) throw new Error("Burn ban network error");
      const burnJson = await burnRes.json();
      const hasBurnBan =
        burnJson.features && burnJson.features.length > 0;

      const burnAlerts = hasBurnBan
        ? [
            {
              id: `burnban-${selectedCounty}`,
              type: "burnBan",
              title: "Burn ban in effect",
              severity: "warning",
              source: "tfs",
              raw: burnJson,
            },
          ]
        : [];

      const nextAlerts = [...weatherAlerts, ...burnAlerts];

      setAlerts(nextAlerts);
      updateDerivedFlags(nextAlerts);
    } catch (e) {
      console.error("Error fetching alerts:", e);
      setAlerts([]);
      resetFlags();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    const id = setInterval(fetchAlerts, 900000); // 15 minutes
    return () => clearInterval(id);
  }, [selectedCounty, countyMapping]);

  useEffect(() => {
    console.log("[AlertsContext] flagsReady changed:", flagsReady);
  }, [flagsReady]);

  return (
    <AlertsContext.Provider
      value={{
        alerts,
        isLoading,
        refreshAlerts: fetchAlerts,
        flags,
        flagsReady,
      }}
    >
      {children}
    </AlertsContext.Provider>
  );
};

export const useAlerts = () => {
  const ctx = useContext(AlertsContext);
  if (!ctx) {
    throw new Error("useAlerts must be used within AlertsProvider");
  }
  return ctx;
};
